import React, {
  ChangeEvent,
  Context,
  createContext,
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useAuth, useDomainPath } from '../../auth/auth.context';
import { get } from '../../fetch';
import { Action } from '../../hooks/action';
import {
  bestEquipmentsForPeople,
  priceOfGroup,
  reservationPrice,
  USER_GROUPS,
  countBookingPeople,
  countBookingChildrenUnderSeven,
} from '@crm/utils';
import { ProvideReservationHash } from './reservationHash.context';
import dayjs from 'dayjs';
import { useChangeEventPartialUpdate } from '../../hooks/useChangeEventUpdate';

/**
 * Types d'actions pour le reducer de réservation
 */
type ReservationAction =
  | Action<Partial<CRM.ReservationDocument>>
  | Action<Partial<CRM.ReservationPayment>>
  | Action<Partial<CRM.ReservationOptions>>;

/**
 * Interface du contexte d'activité de réservation
 */
interface ReservationActivityContext {
  activity?: CRM.PublicActivity;
  activityTimeSlot?: CRM.ActivityTimeSlotDetailed;
  activityTimeSlotCreate?: CRM.ActivityTimeSlotDetailed;
  updateActivity: (activityId: string/*, people?:CRM.BookingPeople*/) => any;
  updateActivityTimeSlot: (activityTimeSlotId: string, monitor?: string) => any;
  updatePricingGroupId: (pricingGroupId: string, activityId?: string) => any;
}

/**
 * Interface du contexte de réservation
 */
interface ReservationContext {
  lastSaveDate?: Date;
  equipmentsError?: string;
  reservation: Partial<CRM.ReservationDocument>;
  groupsPrice?: CRM.ActivityGroupsPrice;
  groupsPriceError: boolean;
  isPendingModification: boolean;
  dispatch: (action: ReservationAction) => void;
  update: (payload: Partial<CRM.ReservationDocument>) => void;
  save: () => Promise<void>;
  reload: () => void;
}

const reservationContext: Context<ReservationContext> = createContext(
  null,
) as any;

const activityContext: Context<ReservationActivityContext> = createContext(
  null,
) as any;

/**
 * Hook pour accéder au contexte de réservation
 * 
 * @returns Contexte de réservation avec état et fonctions
 */
export function useReservation(): ReservationContext {
  const context = useContext(reservationContext);
  return context;
}

/**
 * Hook pour accéder au contexte d'activité de réservation
 * 
 * @returns Contexte d'activité avec état et fonctions
 */
export function useReservationActivity(): ReservationActivityContext {
  const context = useContext(activityContext);
  return context;
}

/**
 * Hook pour les mises à jour d'événements de changement de réservation
 * 
 * @param key Clé du champ à mettre à jour
 * @returns Fonction de gestion des événements de changement
 */
export const useReservationChangeEventUpdate = (
  key: keyof CRM.ReservationDocument,
): ((e: ChangeEvent<{ name?: string; value: string }>) => void) => {
  const { update } = useReservation();
  return useChangeEventPartialUpdate(key, update);
};

/**
 * Reducer pour la gestion de l'état de réservation
 * 
 * Ce reducer gère les actions de mise à jour :
 * - update: mise à jour générale de la réservation
 * - update-payment: mise à jour spécifique du paiement
 * - update-options: mise à jour spécifique des options
 * 
 * @param state État actuel de la réservation
 * @param action Action à exécuter
 * @returns Nouvel état de la réservation
 */
const reducer: Reducer<Partial<CRM.ReservationDocument>, ReservationAction> = (
  state,
  action,
) => {
  console.log('ACTION', action.type, action.payload);
  switch (action.type) {
    case 'update':
      return {
        ...state,
        ...action.payload,
      };
    case 'update-payment':
      return {
        ...state,
        payment: {
          ...state.payment,
          ...(action as Action<Partial<CRM.ReservationPayment>>).payload,
        },
      } as Partial<CRM.ReservationDocument>;
    case 'update-options':
      return {
        ...state,
        options: {
          ...state.options,
          ...(action as Action<Partial<CRM.ReservationOptions>>).payload,
        },
      } as Partial<CRM.ReservationDocument>;
  }
  return state;
};

/**
 * Interface pour les props du provider de réservation
 */
interface Props {
  initialReservation?: CRM.ReservationDocument;
  onSave: (reservation: Partial<CRM.ReservationDocument>) => Promise<void>;
}

/**
 * Provider de contexte de réservation
 * 
 * Ce provider gère :
 * - L'état global de la réservation avec reducer
 * - Les données d'activité et créneaux horaires
 * - Les calculs de prix et équipements automatiques
 * - La sauvegarde et le rechargement
 * 
 * Logique métier :
 * - Calcul automatique des équipements selon les personnes
 * - Calcul automatique des prix selon les groupes utilisateur
 * - Gestion des créneaux horaires supervisés
 * - Mise à jour automatique des totaux de paiement
 * 
 * @param initialReservation Réservation initiale (optionnelle)
 * @param onSave Fonction de sauvegarde personnalisée
 * @param children Composants enfants
 * @returns Provider React du contexte de réservation
 */
export const ProvideReservation: React.FC<Props> = ({
  onSave,
  initialReservation,
  children,
}) => {
  /**
   * Hook pour accéder au contexte de réservation
   * 
   * @returns Contexte de réservation avec état et fonctions de mise à jour
   */
  const useReservation = () => {
    const { userGroup } = useAuth();
    const activityPath = useDomainPath('/activity');
    const activityTimeSlotPath = useDomainPath('/activity-timeslots');
    const reservationPath = useDomainPath('/reservation');
    const [reservation, dispatch] = useReducer(reducer, {
      options: {},
      payment: { amountInEuro: 0, discount: 0, refunds: [], receipts: [] },
      ...initialReservation,
    });
    const [activity, setActivity] = useState<CRM.PublicActivity>();
    const [activityTimeSlot, setActivitytimeSlot] = useState<CRM.ActivityTimeSlotDetailed>();
    const [activityTimeSlotCreate, setActivityTimeSlotCreate] = useState<CRM.ActivityTimeSlotDetailed>();
    const [groupsPrice, setGroupsPrice] = useState<CRM.ActivityGroupsPrice>();
    const [groupsPriceError, setGroupsPriceError] = useState<boolean>(false);
    const [equipmentsError, setEquipmentsError] = useState<string>();
    const [lastSaveDate, setLastSaveDate] = useState<Date>();
    const [isPendingModification, setIsPendingModification] = useState<boolean>(false);

    const reservationId = reservation._id;
    const activityKind = activity?.kind;
    const activityPricing = reservation.pricing || activity?.pricing;

    /**
     * Met à jour la réservation avec de nouvelles données
     * 
     * @param payload Données partielles à mettre à jour
     */
    const update = useCallback((payload: Partial<CRM.ReservationDocument>) => {
      dispatch({ type: 'update', payload });
      setIsPendingModification(true);
    }, []);

    /**
     * Met à jour l'activité de la réservation
     * 
     * @param activityId ID de la nouvelle activité
     * @param _pricing Prix personnalisés (optionnel)
     * @param _pricingGroupId ID du groupe de prix (optionnel)
     */
    const updateActivity = useCallback((activityId: string, _pricing?:CRM.PublicActivityPricing[], _pricingGroupId?:string/*, people?:CRM.BookingPeople*/) => {
      dispatch({
        type: 'update',
        payload: {
          activity: activityId,
          activityDate: undefined,
          activityArrivalDay: undefined,
          activityArrivalDate: undefined,
          pricing: _pricing || undefined,
          pricingGroupId: _pricingGroupId || undefined,
          /*people: people || {},*/
          options: {},
        },
      });
    }, []);

    /**
     * Met à jour le créneau horaire de l'activité
     * 
     * @param activityTimeSlotId ID du créneau horaire
     * @param monitor Moniteur assigné (optionnel)
     */
    const updateActivityTimeSlot = useCallback((activityTimeSlotId:string, monitor?: string) => {
      if(monitor) {
        dispatch({
          type: 'update',
          payload: {
            activityTimeSlot: activityTimeSlotId !== '' ? activityTimeSlotId : undefined,
            options: {...reservation.options, instructors: [monitor], instructorSelectedPriceInEuro: undefined}
          },
        });
      } else {
        dispatch({
          type: 'update',
          payload: {
            activityTimeSlot: activityTimeSlotId !== '' ? activityTimeSlotId : undefined
          },
        });   
      }
      // #TODO fix eventually these dependancies for not causes infinite loop
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Met à jour le groupe de prix et récupère les activités associées
     * 
     * @param pricingGroupId ID du groupe de prix
     * @param _activityId ID de l'activité (optionnel)
     */
    const updatePricingGroupId = useCallback(
      (pricingGroupId: string, _activityId?: string) => {
        get<CRM.PublicActivity[]>(activityPath + '/group/' + pricingGroupId+'?public=true').then((_activities) => {
          const activityId = _activityId ? _activityId : reservation.activity;
          const activity = _activities.find(a => a.id === activityId);
          if (!activity) {// if not found, set to normal price
            get<CRM.PublicActivity>(
              `${activityPath}/${activityId}?public=true`,
            ).then((a) => {
              //setActivity(a);
              const periodPricesForGroup = a.pricing;
              // console.log('UPDATE pricing : ', periodPricesForGroup, a,reservation.activity, activityId);
              //update({ pricingGroupId: USER_GROUPS.NORMAL, pricing: periodPricesForGroup });
              updateActivity(a.id, periodPricesForGroup, pricingGroupId);
            });
          } else {
            const periodPricesForGroup = activity?.pricing;
            // console.log('UPDATE pricing : ', periodPricesForGroup, activity,reservation.activity,_activities, activityId);
            updateActivity(activity?.id, periodPricesForGroup, pricingGroupId);
            update({ pricingGroupId, pricing: periodPricesForGroup });
          }
        });
    },[reservation.activity, update, activityPath, updateActivity]);

    /**
     * Sauvegarde la réservation
     */
    const save = useCallback(() => {

      console.log(reservation.options);
      console.log(reservation.people);

      if(((reservation.options && reservation.options.peopleAtArrivalCount === undefined)
        || (reservation.options && reservation.options.peopleAtArrivalCount === null))
        && reservation.people)
        {
          reservation.options.peopleAtArrivalCount = countBookingPeople(reservation.people);
          console.log(reservation.people);
        }
      setLastSaveDate(new Date());
      setIsPendingModification(false);
      if (reservation.pricing) {
        return onSave(reservation);
      } else {
        return onSave({ ...reservation, pricing: activityPricing });
      }
    }, [onSave, reservation, activityPricing]);

    /**
     * Recharge la réservation depuis le serveur
     */
    const reload = useCallback(() => {
      if (reservationId) {
        get<CRM.ReservationDocument>(
          `${reservationPath}/${reservationId}`,
        ).then(reloaded => dispatch({ type: 'update', payload: reloaded }));
      }
    }, [reservationPath, reservationId]);

    /**
     * Effect pour charger les données d'activité et créneaux horaires
     */
    useEffect(() => {
      if (reservation?.activity) {
        get<CRM.PublicActivity>(
          `${activityPath}/${reservation.activity}?public=true`,
        ).then(setActivity);
      }
      if (reservation?.activityTimeSlot && !reservation?._id) {
        get<CRM.ActivityTimeSlotDetailed>(
          `${activityTimeSlotPath}/detailed/${reservation.activityTimeSlot}`,
        ).then(setActivitytimeSlot);
      } else if(reservation?.activityTimeSlot) {
        get<CRM.ActivityTimeSlotDetailed>(
          `${activityTimeSlotPath}/detailed/${reservation.activityTimeSlot}?reservationIdOmitPeople=`+reservation?._id,
        ).then(setActivitytimeSlot);
      }
    }, [
      update, 
      activityPath, 
      activityTimeSlotPath, 
      reservation.activityTimeSlot,
    reservation.activity,
    reservation?._id
  ]);

  /**
   * Effect pour réinitialiser activityArrivalDate si la nouvelle activité n'exige pas d'horaire de retour
   * 
   * Ne s'applique que si la réservation n'a pas de activityTimeSlot (activités non encadrées)
   */
  useEffect(() => {
    // Attendre que l'activité soit chargée ET qu'elle corresponde à l'activité de la réservation
    if (!activity || !reservation.activity || activity.id !== reservation.activity) {
      return;
    }
    
    // Ne pas réinitialiser si la réservation a un activityTimeSlot (activité encadrée)
    if (reservation.activityTimeSlot) {
      return;
    }
    
    console.log('useEffect activityArrivalDate check - activity:', activity?.name, 'requireArrivalDate:', activity?.requireArrivalDate, 'reservation.activity:', reservation.activity, 'activityArrivalDate:', reservation.activityArrivalDate);
    
    // Si l'activité n'exige pas d'horaire de retour, forcer activityArrivalDate à null
    // Même si c'est undefined dans React (car peut exister en base de données)
    // Ne pas mettre à jour si c'est déjà null pour éviter les boucles infinies
    if (!activity.requireArrivalDate && reservation.activityArrivalDate !== null) {
      // Utiliser null au lieu de undefined pour que Mongoose supprime bien le champ en base
      console.log('Réinitialisation activityArrivalDate - activité:', activity.name, 'requireArrivalDate:', activity.requireArrivalDate, 'activityArrivalDate actuel:', reservation.activityArrivalDate);
      update({ activityArrivalDate: null as any });
    }
  }, [activity, reservation.activity, reservation.activityArrivalDate, reservation.activityTimeSlot, update]);

    /**
     * Effect pour mettre à jour les créneaux horaires et calculer les équipements
     */
    useEffect(() => {
      if(activityTimeSlot) {
        // automatically set hour of arrival from timeslot
        if(
          reservation.activityArrivalDate === undefined || 
          (
            reservation.activityArrivalDate !== undefined && 
            dayjs(reservation.activityArrivalDate).hour() === 0
            )
          ) {
          update({ activityArrivalDate: activityTimeSlot.timeSlotArrival, activityDate: activityTimeSlot.timeSlotDeparture });
        }
        const _bookedPeopleWithMonitor: number = + (
          (reservation.pricing && reservation.people) ? 
          countBookingChildrenUnderSeven(reservation.pricing, reservation.people) : 0
          );

        const _bookedpeople: number = activityTimeSlot.bookedPeople +
          (reservation.people ? countBookingPeople(reservation.people) : 0) /*+ (activityTimeSlot.monitor !== '' ? 1 : 0)*/ /*- _bookedPeopleWithMonitor*/;

          console.log(activityTimeSlot.bookedPeople, _bookedpeople)

        setActivityTimeSlotCreate({
          _id: activityTimeSlot._id,
          timeSlotDeparture: activityTimeSlot.timeSlotDeparture,
          timeSlotArrival: activityTimeSlot.timeSlotArrival,
          monitor: activityTimeSlot.monitor,
          bookedPeople:  _bookedpeople,
          bookedPeopleWithMonitor: activityTimeSlot.bookedPeopleWithMonitor + _bookedPeopleWithMonitor,
          activity: activityTimeSlot.activity,
          minAutonomousPeople: activityTimeSlot.minAutonomousPeople,
          domain: activityTimeSlot.domain,
          constrainReservations: activityTimeSlot.constrainReservations,
          maxPeople: activityTimeSlot.maxPeople,
          maxPeopleWithMonitor: activityTimeSlot.maxPeopleWithMonitor,
          closeReservations: activityTimeSlot.closeReservations
        });
      }
    },[reservation.people, reservation.pricing, activityTimeSlot, reservation.activityArrivalDate, update]);

    /**
     * Effect pour calculer automatiquement les équipements
     */
    useEffect(() => {
      const buildAutoEquipments = () => {
        if (activityPricing && (activityKind === 'canoe' || activityKind === 'supervised')) {
          return bestEquipmentsForPeople(
            activityPricing,
            reservation.people || {},
            activityTimeSlot?.constrainReservations === false,
            (
              activityTimeSlot?.constrainReservations === true && 
              activityTimeSlot?.minAutonomousPeople &&
              !userGroup?.ignoreSupervisedChildRestrictions
            ) ? (activityTimeSlot?.minAutonomousPeople) : 0,
            activityTimeSlot?.maxPeopleWithMonitor || 1000,
            activityTimeSlot?.bookedPeopleWithMonitor
          );
        }
        return undefined;
      };

      if (!reservation.options?.equipments?.customized) {
        try {
          dispatch({
            type: 'update-options',
            payload: { equipments: buildAutoEquipments() },
          });
          setEquipmentsError(undefined);
        } catch (error) {
          setEquipmentsError(error instanceof Error ? error.message : 'Erreur inconnue');
        }
      }
    }, [
      update,
      reservation.options?.equipments?.customized,
      reservation.people,
      activityKind,
      activityPricing,
      activityTimeSlot,
      userGroup?.ignoreSupervisedChildRestrictions
    ]);

    /**
     * Effect pour calculer automatiquement les prix selon le groupe utilisateur
     */
    useEffect(() => {
      if (activityPricing && activity?.pricingPeriods && userGroup?.userGroupId) {
        const pricingPeriods = {
          [dayjs().year()]: {
            periods: activity.pricingPeriods,
            exceptions: activity.pricingPeriodsExceptions,
          } as CRM.YearlyPricingPeriods,
        };
        const priceForUserGroup = (userGroupId: string) =>
          priceOfGroup(
            activityPricing,
            pricingPeriods,
            reservation.people || {},
            userGroupId,
            reservation.activityDate || new Date(),
          );

        try {
          const group = reservation.pricingGroupId || userGroup?.userGroupId;
          let bestPrice = priceForUserGroup(group);
          if (!bestPrice || bestPrice.groups.length === 0) {
            bestPrice = priceForUserGroup(USER_GROUPS.NORMAL);
            dispatch({
              type: 'update',
              payload: { pricingGroupId: USER_GROUPS.NORMAL },
            });
          } else {
            dispatch({
              type: 'update',
              payload: { pricingGroupId: group },
            });
          }
          setGroupsPrice(bestPrice);
        } catch (e) {
          console.error('Error while getting the pricing', e);
          setGroupsPriceError(true);
        }
        //
        // const price = reservationPrice(
        //   bestPrice,
        //   reservation.options?.instructors!,
        //   activity.instructorPriceInEuro,
        // );
        //
        // dispatch({
        //   type: 'update-payment',
        //   payload: { amountInEuro: price.totalPrice },
        // });
      }
    }, [
      userGroup?.userGroupId,
      reservation.people,
      reservation.activityDate,
      reservation.pricingGroupId,
      activityPricing,
      activity,
    ]);

    /**
     * Effect pour mettre à jour le prix total de la réservation
     */
    useEffect(() => {
      if (!activity || !groupsPrice) {
        return;
      }

      const price = reservationPrice(
        groupsPrice,
        reservation.options?.instructors!,
        reservation.options?.instructorSelectedPriceInEuro || activity.instructorPriceInEuro,
        reservation.payment?.refunds || [],
        reservation.payment?.discount || 0,
      );

      dispatch({
        type: 'update-payment',
        payload: { amountInEuro: price.totalPrice },
      });
    }, [
      groupsPrice,
      reservation.options?.instructors,
      reservation.options?.instructorSelectedPriceInEuro,
      activity,
      reservation.payment?.refunds,
      reservation.payment?.discount,
    ]);

    /**
     * Retourne le provider avec les contextes d'activité et de réservation
     */
    return (
      <activityContext.Provider value={{ activity, activityTimeSlot, activityTimeSlotCreate, updateActivity, updateActivityTimeSlot, updatePricingGroupId }}>
        <reservationContext.Provider
          value={{
            lastSaveDate,
            equipmentsError,
            reservation,
            groupsPrice,
            groupsPriceError,
            isPendingModification,
            dispatch,
            update,
            save,
            reload,
          }}
        >
          <ProvideReservationHash>{children}</ProvideReservationHash>
        </reservationContext.Provider>
      </activityContext.Provider>
    );
  };

  return useReservation();
};
